import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Camera, Loader2, Sparkles, X, Check } from "lucide-react";
import { toast } from "sonner";

const COST_CATEGORIES = [
  "Materials", "Labor", "Subcontractor", "Permits & Fees",
  "Equipment & Rental", "Fuel & Travel", "Office & Overhead", "Other",
];

const today = () => new Date().toISOString().slice(0, 10);

export default function ReceiptCapture({ fixedJob = null }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [reading, setReading] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [form, setForm] = useState({
    direction: "debit", amount: "", vendor: "", date: today(),
    jobId: fixedJob?.id || "", cost_category: "Materials",
  });

  const { data: jobsData } = useQuery({
    queryKey: ["jobs"],
    enabled: !fixedJob,
    queryFn: async () => (await api.get("/jobs")).data,
  });
  const allJobs = Array.isArray(jobsData) ? jobsData : (jobsData?.jobs || []);
  const jobs = allJobs.filter((j) => (j.status || "Active") === "Active");

  const setF = (patch) => setForm((p) => ({ ...p, ...patch }));

  const reset = () => {
    setFile(null); setPreview(""); setAiNote("");
    setForm({ direction: "debit", amount: "", vendor: "", date: today(), jobId: fixedJob?.id || "", cost_category: "Materials" });
    if (inputRef.current) inputRef.current.value = "";
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : "");
    setReading(true);
    setAiNote("");
    try {
      const body = new FormData();
      body.append("file", f);
      const { data } = await api.post("/receipts/read", body, { timeout: 120000 });
      if (data?.ai) {
        setF({
          direction: data.direction || "debit",
          amount: data.amount ? String(data.amount) : "",
          vendor: data.vendor || "",
          date: data.date || today(),
          cost_category: data.cost_category || "Materials",
        });
        setAiNote(data.rationale || "Receipt read — check the details and pick the job.");
      } else {
        setAiNote("Couldn't auto-read this one — just fill in the details below.");
      }
    } catch (err) {
      setAiNote("Couldn't auto-read this one — just fill in the details below.");
    } finally {
      setReading(false);
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append("file", file);
      body.append("job_id", form.jobId);
      body.append("direction", form.direction);
      body.append("amount", form.amount);
      body.append("vendor", form.vendor);
      body.append("date", form.date);
      body.append("cost_category", form.cost_category);
      return (await api.post("/receipts/capture", body, { timeout: 120000 })).data;
    },
    onSuccess: (d) => {
      toast.success(d.kind === "deposit" ? "Receipt saved & deposit recorded" : "Receipt saved to the job");
      qc.invalidateQueries({ queryKey: ["bank-transactions"] });
      qc.invalidateQueries({ queryKey: ["financials-overview"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      reset();
    },
    onError: async (e) => toast.error(await formatApiError(e, "Could not save the receipt.")),
  });

  const canSave = file && form.jobId && Number(form.amount) > 0 && !reading;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="receipt-capture">
      <div className="flex items-center gap-2 font-semibold text-[#0B3A8F]"><Camera size={18} /> Snap a receipt</div>
      <p className="mt-1 text-sm text-[#4B6370]">
        Take a photo (or pick a photo/PDF) of a receipt. We'll read it, you pick the job, and it's filed to that
        customer's Google Drive folder with the expense for taxes.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={onPick}
        data-testid="receipt-file-input"
      />

      {!file ? (
        <Button className="mt-3 h-10 bg-[#0B3A8F]" onClick={() => inputRef.current?.click()} data-testid="receipt-snap-btn">
          <Camera size={16} className="mr-2" /> Snap or choose receipt
        </Button>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="flex items-start gap-3">
            {preview ? (
              <img src={preview} alt="Receipt" className="h-20 w-20 rounded-lg border border-slate-200 object-cover" data-testid="receipt-preview" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-xs text-[#4B6370]">PDF</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-[#061A23]">{file.name}</div>
              {reading ? (
                <div className="mt-1 flex items-center gap-1 text-xs text-[#0B3A8F]"><Loader2 size={13} className="animate-spin" /> Reading receipt…</div>
              ) : aiNote ? (
                <div className="mt-1 flex items-start gap-1 text-xs text-[#4B6370]"><Sparkles size={13} className="mt-0.5 text-[#0B3A8F]" /> {aiNote}</div>
              ) : null}
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={reset} data-testid="receipt-cancel-btn"><X size={16} /></Button>
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <label className="text-xs font-semibold text-[#4B6370]">Type</label>
              <Select value={form.direction} onValueChange={(v) => setF({ direction: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="receipt-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">Purchase (money out)</SelectItem>
                  <SelectItem value="credit">Payment received (money in)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#4B6370]">Amount</label>
              <Input type="number" value={form.amount} onChange={(e) => setF({ amount: e.target.value })} placeholder="0.00" className="mt-1 h-9" data-testid="receipt-amount" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#4B6370]">{form.direction === "credit" ? "Paid by / from" : "Vendor"}</label>
              <Input value={form.vendor} onChange={(e) => setF({ vendor: e.target.value })} placeholder={form.direction === "credit" ? "Customer / payer" : "Store name"} className="mt-1 h-9" data-testid="receipt-vendor" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#4B6370]">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setF({ date: e.target.value })} className="mt-1 h-9" data-testid="receipt-date" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#4B6370]">Job (open & active)</label>
              {fixedJob ? (
                <div className="mt-1 flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-[#061A23]" data-testid="receipt-job-fixed">
                  {fixedJob.label}
                </div>
              ) : (
                <Select value={form.jobId} onValueChange={(v) => setF({ jobId: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="receipt-job"><SelectValue placeholder="Choose the job this is for" /></SelectTrigger>
                  <SelectContent>
                    {jobs.length === 0 && <div className="px-2 py-1.5 text-sm text-[#4B6370]">No active jobs</div>}
                    {jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>{(j.job_number || "Job")} — {j.client_name || j.name || ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {form.direction === "debit" && (
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#4B6370]">Cost category</label>
                <Select value={form.cost_category} onValueChange={(v) => setF({ cost_category: v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="receipt-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COST_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Button className="h-10 w-full bg-emerald-600 hover:bg-emerald-700 sm:w-auto" disabled={!canSave || save.isPending} onClick={() => save.mutate()} data-testid="receipt-save-btn">
            {save.isPending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />}
            Save receipt to job
          </Button>
        </div>
      )}
    </div>
  );
}
